import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { inventoryAPI, mergeAPI } from '../../../services/api';
import i18n from '../../../i18n';

// Async thunk для загрузки модулей
export const fetchModules = createAsyncThunk(
  'inventory/fetchModules',
  async (type = null, { rejectWithValue }) => {
    try {
      
      const data = await inventoryAPI.getModules(type);
      
      return {
        type,
        data: data.modules || [],
        totalCount: data.totalCount || 0,
        newCount: data.newCount || 0
      };
    } catch (error) {
      console.error('Redux: Error fetching modules:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk для отметки модулей как просмотренных
export const markModulesAsViewed = createAsyncThunk(
  'inventory/markModulesAsViewed',
  async (moduleIds, { rejectWithValue }) => {
    try {
      
      const response = await inventoryAPI.markModulesAsViewed(moduleIds);
      
      return moduleIds;
    } catch (error) {
      console.error('Redux: Error marking modules as viewed:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk для отметки одного модуля как просмотренного
export const markModuleAsViewed = createAsyncThunk(
  'inventory/markModuleAsViewed',
  async (moduleId, { rejectWithValue }) => {
    try {
      
      const response = await inventoryAPI.markModuleAsViewed(moduleId);
      
      return { moduleId, response };
    } catch (error) {
      console.error('Redux: Error marking single module as viewed:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Merge rank configs (+ the user's Laboratory discount already applied)
export const fetchModuleRanks = createAsyncThunk(
  'inventory/fetchModuleRanks',
  async (_, { rejectWithValue }) => {
    try {
      return await mergeAPI.getRanks();
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

const initialState = {
  // Данные по типам модулей
  modules: {
    all: [],
    HEAD: [],
    BODY: [],
    HAND: [],
    LEG: []
  },
  
  // Метаданные
  totalCount: 0,
  newCount: 0,
  
  // Состояние загрузки
  isLoading: false,
  error: null,

  // Module ranks (stat multiplier, merge discount of the Laboratory)
  ranks: [],
  ranksLoaded: false,
  ranksError: null,
  mergeDiscount: 0,
};

const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateModuleNewStatus: (state, action) => {
      const { moduleId, isNew } = action.payload;
      
      // Обновляем статус во всех типах модулей
      Object.keys(state.modules).forEach(type => {
        const module = state.modules[type].find(m => m.id === moduleId);
        if (module) {
          module.isNew = isNew;
        }
      });
      
      // Обновляем счетчик новых модулей
      if (isNew) {
        state.newCount += 1;
      } else {
        state.newCount = Math.max(0, state.newCount - 1);
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch modules
      .addCase(fetchModules.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchModules.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        
        const { type, data, totalCount, newCount } = action.payload;
        
        // Если загружаем все модули (type = null), разделяем их по типам
        if (!type) {
          
          // Очищаем все категории
          state.modules.all = data;
          state.modules.HEAD = [];
          state.modules.BODY = [];
          state.modules.HAND = [];
          state.modules.LEG = [];
          
          // Разделяем модули по типам
          data.forEach(module => {
            // Используем РЕАЛЬНУЮ структуру данных от бэкенда
            const moduleType = module.module?.type.replace('LEFT_','').replace('RIGHT_','');
            if (state.modules[moduleType]) {
              state.modules[moduleType].push(module);
            }
          });
          
          state.totalCount = state.modules.all.length;
        } else {
          // Загружаем модули конкретного типа
          const moduleType = type;
          state.modules[moduleType] = data;
        }
        
        state.totalCount = totalCount;
        state.newCount = newCount;
      })
      .addCase(fetchModules.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        console.error('Redux: Failed to fetch modules:', action.payload);
      })
      
      // Mark modules as viewed
      .addCase(markModulesAsViewed.pending, (state) => {
        // Можно добавить состояние загрузки для этой операции
      })
      .addCase(markModulesAsViewed.fulfilled, (state, action) => {
        const moduleIds = action.payload;
        
        // Обновляем статус модулей
        moduleIds.forEach(moduleId => {
          Object.keys(state.modules).forEach(type => {
            const module = state.modules[type].find(m => m.id === moduleId);
            if (module && module.isNew) {
              module.isNew = false;
              state.newCount = Math.max(0, state.newCount - 1);
            }
          });
        });
        
      })
      .addCase(markModulesAsViewed.rejected, (state, action) => {
        state.error = action.payload;
        console.error('Redux: Failed to mark modules as viewed:', action.payload);
      })
      
      // Mark single module as viewed
      .addCase(markModuleAsViewed.pending, (state) => {
        // Можно добавить состояние загрузки для этой операции
      })
      .addCase(markModuleAsViewed.fulfilled, (state, action) => {
        const { moduleId, response } = action.payload;
        
        if (response.success) {
          // Обновляем статус модуля
          Object.keys(state.modules).forEach(type => {
            const module = state.modules[type].find(m => m.id === moduleId);
            if (module && module.isNew) {
              module.isNew = false;
              state.newCount = Math.max(0, state.newCount - 1);
            }
          });
          
        }
      })
      .addCase(markModuleAsViewed.rejected, (state, action) => {
        state.error = action.payload;
        console.error('Redux: Failed to mark single module as viewed:', action.payload);
      })

      .addCase(fetchModuleRanks.fulfilled, (state, action) => {
        state.ranks = action.payload?.ranks || [];
        state.mergeDiscount = action.payload?.mergeDiscount || 0;
        state.ranksLoaded = true;
        state.ranksError = null;
      })
      .addCase(fetchModuleRanks.rejected, (state, action) => {
        state.ranksLoaded = true;
        state.ranksError = action.payload || i18n.t('merge.rules_failed', 'Failed to load merge rules');
      });
  }
});

export const { clearError, updateModuleNewStatus } = inventorySlice.actions;
export default inventorySlice.reducer;
